import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { GetMocktestQuestion } from "../../../Client/request";
import PteAttemptContext from "../PteAttemptContext";
import { getListeningAudioUrl, getQuestionId, preloadAudioUrl } from "../utils/listeningPreload";
import { getSpeakingConfig } from "../utils/speakingConfig";

export const useMockQuestionPreload = (questions = [], currentStep = 1, currentAttemptId = null, setStep) => {
  const resumeAttemptId = useContext(PteAttemptContext);
  const attemptId = currentAttemptId || resumeAttemptId;
  const currentQuestion = questions[currentStep - 1];
  const currentKey = `${attemptId}:${getQuestionId(currentQuestion)}`;
  const cacheRef = useRef({});
  const [questionCache, setQuestionCache] = useState({});
  const [failedQuestion, setFailedQuestion] = useState(null);
  const completedAudioRef = useRef(new Set());
  const [audioLoading, setAudioLoading] = useState(null);
  const [saving, setSaving] = useState(null);
  const [questionTransition, setQuestionTransition] = useState(null);
  const transitionRef = useRef(null);

  useEffect(() => () => {
    transitionRef.current?.controller.abort();
    transitionRef.current = null;
  }, [currentKey]);
  const hasCurrentAudio = currentQuestion?.Category === "Listening" ||
    (currentQuestion?.Category === "Speaking" && getSpeakingConfig(currentQuestion.SubCategory).hasAudio);

  const onAudioLoadingChange = useCallback((busy) => {
    setAudioLoading(previous => previous?.key === currentKey && previous.busy === busy
      ? previous : { key: currentKey, busy });
  }, [currentKey]);
  const onSavingChange = useCallback((busy) => {
    setSaving(previous => previous?.key === currentKey && previous.busy === busy
      ? previous : { key: currentKey, busy });
  }, [currentKey]);

  const backgroundQuestions = useMemo(() => {
    const remaining = [];
    for (const question of questions.slice(currentStep)) {
      if (question.Category !== currentQuestion?.Category) break;
      if (question.Category === "Speaking" && !getSpeakingConfig(question.SubCategory).hasAudio) continue;
      remaining.push(question);
      if (remaining.length === 2) break;
    }
    return remaining;
  }, [questions, currentStep, currentQuestion?.Category]);

  const ensureQuestionData = useCallback(async (question, signal) => {
    const key = `${attemptId}:${getQuestionId(question)}`;
    if (cacheRef.current[key]) return cacheRef.current[key];
    const response = await GetMocktestQuestion(question.QuestionId, attemptId, { signal, timeout: 30000 });
    if (signal.aborted) return null;
    if (response?.data) {
      cacheRef.current = { ...cacheRef.current, [key]: response.data };
      setQuestionCache(cacheRef.current);
      return response.data;
    }
    return null;
  }, [attemptId]);

  // The foreground request never waits for future questions or their audio.
  useEffect(() => {
    if (!currentQuestion) return;
    const controller = new AbortController();
    ensureQuestionData(currentQuestion, controller.signal).then(data => {
      if (!data && !controller.signal.aborted) setFailedQuestion(currentKey);
    }).catch(() => {
      if (!controller.signal.aborted) setFailedQuestion(currentKey);
    });
    return () => controller.abort();
  }, [currentQuestion, currentKey, ensureQuestionData]);

  const canPreload = !!questionCache[currentKey] &&
    questionTransition?.from !== currentStep &&
    (!hasCurrentAudio || (audioLoading?.key === currentKey && !audioLoading.busy)) &&
    !(saving?.key === currentKey && saving.busy);

  useEffect(() => {
    if (!canPreload) return;
    const controller = new AbortController();
    const { signal } = controller;
    const preload = async () => {
      for (const question of backgroundQuestions) {
        if (signal.aborted) return;
        const data = await ensureQuestionData(question, signal);
        if (signal.aborted) return;
        const url = getListeningAudioUrl(data);
        if (!url || completedAudioRef.current.has(url)) continue;
        const status = await preloadAudioUrl(url, { signal });
        if (signal.aborted) return;
        if (status === "ready") completedAudioRef.current.add(url);
      }
    };
    preload().catch(() => {}); // Foreground loading still retries an unsuccessful prefetch.
    return () => controller.abort();
  }, [backgroundQuestions, canPreload, currentKey, ensureQuestionData]);

  const getCachedQuestionData = useCallback(
    question => questionCache[`${attemptId}:${getQuestionId(question)}`] || null,
    [questionCache, attemptId]
  );

  const changeQuestion = useCallback(async (update) => {
    if (transitionRef.current) return;
    const nextStep = typeof update === "function" ? update(currentStep) : update;
    const next = questions[nextStep - 1];
    if (!next || nextStep === currentStep) return;
    if (next.Category !== currentQuestion?.Category) {
      setStep(nextStep);
      return;
    }
    const controller = new AbortController();
    transitionRef.current = { controller, step: nextStep, error: false };
    setQuestionTransition({ from: currentStep, error: false });
    try {
      const data = await ensureQuestionData(next, controller.signal);
      if (controller.signal.aborted) return;
      if (!data) throw new Error("Question unavailable");
      setStep(nextStep);
      setQuestionTransition(null);
      transitionRef.current = null;
    } catch {
      if (!controller.signal.aborted) {
        transitionRef.current.error = true;
        setQuestionTransition({ from: currentStep, error: true });
      }
    }
  }, [questions, currentStep, currentQuestion?.Category, ensureQuestionData, setStep]);

  const retryQuestion = useCallback(() => {
    if (!transitionRef.current?.error) return;
    const nextStep = transitionRef.current.step;
    transitionRef.current = null;
    return changeQuestion(nextStep);
  }, [changeQuestion]);

  return {
    getCachedQuestionData,
    changeQuestion,
    questionTransition: questionTransition?.from === currentStep ? questionTransition : null,
    retryQuestion,
    isCurrentCategoryReady: !!questionCache[currentKey] || failedQuestion === currentKey,
    onAudioLoadingChange,
    onSavingChange,
  };
};
